import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { useRouters } from '../api/queries';
import type { RouterDto } from '../api/types';

const STORAGE_KEY = 'mikromanager.currentRouterId';

interface CurrentRouterState {
  /** '' sólo mientras cargan los routers o si no hay ninguno. */
  routerId: string;
  router: RouterDto | null;
  routers: RouterDto[];
  loading: boolean;
  setRouterId: (id: string) => void;
}

const CurrentRouterContext = createContext<CurrentRouterState | null>(null);

/**
 * Router de trabajo de todo el panel.
 *
 * Las pantallas operativas muestran un único router a la vez: con «Todos los routers»
 * se mezclaban usuarios, lotes, sesiones y planes de equipos distintos en la misma lista.
 * La elección se recuerda entre recargas.
 */
export function CurrentRouterProvider({ children }: { children: ReactNode }) {
  const routers = useRouters();
  const [stored, setStored] = useState(readStored);

  const list = useMemo(() => routers.data ?? [], [routers.data]);
  // Si el guardado ya no existe (borrado, o el usuario perdió el acceso) se cae al primero.
  const router = list.find((item) => item.id === stored) ?? list[0] ?? null;

  const setRouterId = useCallback((id: string) => {
    setStored(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Sin almacenamiento (modo privado) la elección dura lo que la pestaña.
    }
  }, []);

  const value = useMemo<CurrentRouterState>(
    () => ({
      routerId: router?.id ?? '',
      router,
      routers: list,
      loading: routers.isLoading,
      setRouterId,
    }),
    [router, list, routers.isLoading, setRouterId],
  );

  return <CurrentRouterContext.Provider value={value}>{children}</CurrentRouterContext.Provider>;
}

export function useCurrentRouter(): CurrentRouterState {
  const context = useContext(CurrentRouterContext);
  if (!context) throw new Error('useCurrentRouter fuera de CurrentRouterProvider');
  return context;
}

function readStored(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}
